/** The on-page cross-origin sandboxed call iframe. */
import { configureFrame } from './frame-attributes';
import { LoadingPuck, PUCK_SIZE_PX } from './loading-puck';
import type { ToFrameMessage } from './protocol';

const CONTAINER_ID = 'quarterzip-sdk-root';

/** Gap between the panel and the corner of the viewport. */
const VIEWPORT_MARGIN = 20;

const TRANSITION_MS_VAR = '--qz-transition-ms';

const TRANSITION_PROPERTIES = ['width', 'height', 'border-radius'] as const;

/** The iframe's size until the frame reports its own; the document must lay out against a real width. */
const INITIAL_SIZE = { width: 265, height: 276, radius: 14, durationMs: 0 };

/** Above the host page's own fixed UI, leaving 646 free for an integrator to stack over us. */
const PANEL_Z_INDEX = 2147483001;

/** Drawn on the container, which an exactly-sized iframe would clip. */
const PANEL_SHADOW = [
	'0.5px 0.5px 0.8px hsl(220deg 50% 30% / 0.12)',
	'2px 1.9px 3.1px -0.4px hsl(220deg 50% 30% / 0.12)',
	'3.5px 3.3px 5.4px -0.7px hsl(220deg 50% 30% / 0.12)',
	'5.6px 5.4px 8.8px -1.1px hsl(220deg 50% 30% / 0.12)',
	'8.9px 8.4px 13.8px -1.4px hsl(220deg 50% 30% / 0.12)',
	'13.8px 13.1px 21.4px -1.8px hsl(220deg 50% 30% / 0.12)',
	'20.9px 19.8px 32.4px -2.1px hsl(220deg 50% 30% / 0.12)',
	'30.7px 29.2px 47.7px -2.5px hsl(220deg 50% 30% / 0.12)'
].join(', ');

/**
 * Matches shadow to keep the transition soft
 */
const PANEL_SHADOW_FILL = 'hsl(220deg 50% 30% / 0.4)';

export class CallFrame {
	readonly container: HTMLDivElement;
	readonly iframe: HTMLIFrameElement;
	private readonly origin: string;

	private placeholder: LoadingPuck | null;

	/** Applied to the window only once revealed. */
	private geometry = { ...INITIAL_SIZE };

	private revealed = false;

	/** Viewport position of the box when the current drag began; null when not dragging. */
	private dragOrigin: { x: number; y: number } | null = null;

	/** Tears down the missed-release watch. Null whenever no drag is live. */
	private stopDragWatch: (() => void) | null = null;

	/** Tears down the viewport watch. Lives for as long as the panel does. */
	private stopViewportWatch: (() => void) | null = null;

	constructor(callUrl: string, origin: string) {
		this.origin = origin;

		const container = document.createElement('div');
		container.id = CONTAINER_ID;
		Object.assign(container.style, {
			position: 'fixed',
			bottom: `${VIEWPORT_MARGIN}px`,
			right: `${VIEWPORT_MARGIN}px`,
			// This is the window the panel is revealed through, not the panel.
			width: `${PUCK_SIZE_PX}px`,
			height: `${PUCK_SIZE_PX}px`,
			maxWidth: '100vw',
			maxHeight: '100vh',
			zIndex: `${PANEL_Z_INDEX}`,
			border: 'none',
			background: 'transparent',
			borderRadius: `${PUCK_SIZE_PX / 2}px`,
			overflow: 'hidden',
			boxShadow: 'none',
			transition: TRANSITION_PROPERTIES.map(
				(prop) => `${prop} var(${TRANSITION_MS_VAR}) ease`
			).join(', ')
		} satisfies Partial<CSSStyleDeclaration>);
		// Only the variable is ever rewritten: changing transition-property and a transitioned value
		// together is what makes a transition silently not fire.
		container.style.setProperty(TRANSITION_MS_VAR, '0ms');

		const iframe = document.createElement('iframe');
		Object.assign(iframe.style, {
			// Full size, not 100%: clipping is paint-only, so the document never lays out against 48px.
			position: 'absolute',
			bottom: '0',
			right: '0',
			width: `${INITIAL_SIZE.width}px`,
			height: `${INITIAL_SIZE.height}px`,
			border: 'none',
			background: 'transparent',
			colorScheme: 'normal',
			// Same variable as the window: without this a minimise snaps while the window eases, and the
			// gap above the bottom-pinned iframe shows PUCK_BG as a bar sliding over the panel.
			transition: `width var(${TRANSITION_MS_VAR}) ease, height var(${TRANSITION_MS_VAR}) ease`,
			pointerEvents: 'none'
		} satisfies Partial<CSSStyleDeclaration>);
		configureFrame(iframe, callUrl, 'call');

		const placeholder = new LoadingPuck(document);
		container.appendChild(iframe);
		container.appendChild(placeholder.element);
		document.body.appendChild(container);

		this.container = container;
		this.iframe = iframe;
		this.placeholder = placeholder;
		this.watchViewport();
	}

	/** Re-place the box when the viewport changes size, so it stays inside it. */
	private watchViewport(): void {
		const onResize = (): void => {
			const { left, top } = this.container.getBoundingClientRect();
			this.applyPosition(left, top);
		};
		window.addEventListener('resize', onResize);
		this.stopViewportWatch = () => window.removeEventListener('resize', onResize);
	}

	/** Whether a message event genuinely originates from this frame. */
	isFromFrame(event: MessageEvent): boolean {
		return event.origin === this.origin && event.source === this.iframe.contentWindow;
	}

	/** Post a control-plane message to the frame with an exact target origin (never '*'). */
	post(message: ToFrameMessage, transfer?: Transferable[]): void {
		this.iframe.contentWindow?.postMessage(message, this.origin, transfer ?? []);
	}

	resize(width: number, height: number, radius: number, durationMs: number): void {
		this.geometry = { width, height, radius, durationMs };
		this.iframe.style.width = `${width}px`;
		this.iframe.style.height = `${height}px`;
		if (this.revealed) this.applyGeometry();
	}

	reveal(): void {
		if (this.revealed) return;
		this.revealed = true;
		this.placeholder?.dismiss();
		this.container.style.boxShadow = PANEL_SHADOW;
		this.container.style.backgroundColor = PANEL_SHADOW_FILL;
		this.iframe.style.pointerEvents = 'auto';
		this.applyGeometry();
	}

	private applyGeometry(): void {
		const { width, height, radius, durationMs } = this.geometry;
		this.container.style.setProperty(TRANSITION_MS_VAR, `${durationMs}ms`);
		this.container.style.width = `${width}px`;
		this.container.style.height = `${height}px`;
		this.container.style.borderRadius = `${radius}px`;
	}

	/** Pin the box to explicit `left`/`top`, so the relayed deltas have a fixed reference. */
	beginDrag(): void {
		const rect = this.container.getBoundingClientRect();
		this.dragOrigin = { x: rect.left, y: rect.top };
		this.applyPosition(rect.left, rect.top);
		this.watchForMissedRelease();
	}

	/** Move the box by a delta from where the drag started. */
	dragBy(dx: number, dy: number): void {
		if (!this.dragOrigin) return;
		this.applyPosition(this.dragOrigin.x + dx, this.dragOrigin.y + dy);
	}

	/** Ends the drag when the frame does not report the end itself: pointer events reach this document
	 * once a gesture is over, so a release seen here is the signal. */
	private watchForMissedRelease(): void {
		this.stopDragWatch?.();

		// A move with no button held is the release, observed late.
		const onMove = (event: PointerEvent): void => {
			if (event.buttons === 0) this.endDrag();
		};
		const onRelease = (): void => this.endDrag();
		// A hidden tab never delivers the release.
		const onHidden = (): void => {
			if (document.visibilityState === 'hidden') this.endDrag();
		};

		window.addEventListener('pointermove', onMove, true);
		window.addEventListener('pointerup', onRelease, true);
		window.addEventListener('pointercancel', onRelease, true);
		document.addEventListener('visibilitychange', onHidden);

		this.stopDragWatch = () => {
			window.removeEventListener('pointermove', onMove, true);
			window.removeEventListener('pointerup', onRelease, true);
			window.removeEventListener('pointercancel', onRelease, true);
			document.removeEventListener('visibilitychange', onHidden);
		};
	}

	endDrag(): void {
		this.dragOrigin = null;
		this.stopDragWatch?.();
		this.stopDragWatch = null;
	}

	private get boxSize(): { width: number; height: number } {
		return this.revealed ? this.geometry : { width: PUCK_SIZE_PX, height: PUCK_SIZE_PX };
	}

	/** Place the box at a viewport position, clamped inside it and held by its nearer edges. */
	private applyPosition(x: number, y: number): void {
		const { width, height } = this.boxSize;
		const style = this.container.style;
		[style.left, style.right] = holdNearerEdge(x, width, viewportWidth());
		[style.top, style.bottom] = holdNearerEdge(y, height, viewportHeight());
	}

	destroy(): void {
		// Before anything else: a torn-down session must not leave its window listeners behind.
		this.endDrag();
		this.stopViewportWatch?.();
		this.stopViewportWatch = null;
		this.placeholder?.destroy();
		this.placeholder = null;
		this.container.remove();
	}
}

const clamp = (value: number, min: number, max: number): number =>
	Math.max(min, Math.min(value, Math.max(min, max)));

const viewportWidth = (): number => document.documentElement.clientWidth;
const viewportHeight = (): number => document.documentElement.clientHeight;

/** Snapped to a whole device pixel: a fractional offset antialiases the box's edges. */
const snapToDevicePixel = (value: number): number => {
	const dpr = window.devicePixelRatio || 1;
	return Math.round(value * dpr) / dpr;
};

/** One axis, clamped into the viewport, as its `[left, right]` or `[top, bottom]` pair. The nearer
 * edge holds it, so the box stays against that edge when either size changes. */
const holdNearerEdge = (
	offset: number,
	extent: number,
	viewport: number
): [start: string, end: string] => {
	const start = snapToDevicePixel(clamp(offset, 0, viewport - extent));
	return start + extent / 2 < viewport / 2
		? [`${start}px`, 'auto']
		: ['auto', `${viewport - start - extent}px`];
};
